import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, isSameWeek, differenceInWeeks, startOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import CreateLottoModal from '../components/lotto/CreateLottoModal';
import DrawAnimation from '../components/lotto/DrawAnimation';
import { DonkatsuLoading, SkeletonCard } from '../components/common/LoadingStates';
import { EmptyLotto } from '../components/common/EmptyStates';
import { FadeInPage } from '../components/common/PageTransition';
import { SwipeableCard } from '../components/common/SwipeableCard';
import { useLotto } from '../contexts/LottoContext';
import { useAuth } from '../contexts/AuthContext';

const LottoPage: React.FC = () => {
  const { lottos, isLoading, addLotto, applyToLotto, completeLotto, drawResult, showAnimation, closeAnimation } = useLotto();
  const { user } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  
  // 주간 페이지네이션 상태
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    startOfWeek(new Date(), { weekStartsOn: 0 }) // 일요일 시작
  );

  // 🔍 디버깅
  console.log('📊 LottoPage - lottos:', lottos);
  console.log('📊 LottoPage - lottos.length:', lottos?.length);

  // 진행 중 (미완료) 로또
  const activeLottos = lottos.filter(l => !l.isCompleted);
  // 완료된 로또
  const completedLottos = lottos.filter(l => l.isCompleted);

  console.log('📊 activeLottos:', activeLottos);
  console.log('📊 completedLottos:', completedLottos);

  // 이번 주 필터링된 완료 로또
  const filteredCompletedLottos = useMemo(() => {
    const weekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 0 });
    
    return completedLottos.filter((lotto) => {
      const lottoDate = new Date(lotto.date);
      return lottoDate >= currentWeekStart && lottoDate <= weekEnd;
    });
  }, [completedLottos, currentWeekStart]);

  // 주간 네비게이션
  const handlePrevWeek = () => {
    setCurrentWeekStart(prev => subWeeks(prev, 1));
  };

  const handleNextWeek = () => {
    const nextWeek = addWeeks(currentWeekStart, 1);
    const thisWeek = startOfWeek(new Date(), { weekStartsOn: 0 });
    
    // 이번 주보다 미래로 못 감
    if (nextWeek <= thisWeek) {
      setCurrentWeekStart(nextWeek);
    }
  };

  const handleThisWeek = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }));
  };

  const isThisWeek = isSameWeek(currentWeekStart, new Date(), { weekStartsOn: 0 });

  // 주차 계산
  const monthStart = startOfMonth(currentWeekStart);
  const weekOfMonth = differenceInWeeks(currentWeekStart, monthStart) + 1;

  const handleCreateLotto = async (date: Date, maxSlots: number) => {
    try {
      await addLotto(date, maxSlots);
      setIsCreateModalOpen(false);
    } catch (error) {
      // 에러는 Context에서 Toast로 표시
    }
  };

  const handleApply = async (lottoId: string) => {
    const reason = prompt('신청 사유를 입력해주세요:');
    if (reason) {
      try {
        await applyToLotto(lottoId, reason);
      } catch (error) {
        // 에러는 Context에서 Toast로 표시
      }
    }
  };

  const handleDraw = async (lottoId: string) => {
    if (window.confirm('추첨을 시작하시겠습니까?')) {
      try {
        await completeLotto(lottoId);
      } catch (error) {
        // 에러는 Context에서 Toast로 표시
      }
    }
  };

  return (
    <FadeInPage>
      <main className="container mx-auto px-4 py-12 pb-0">
      <div className="max-w-6xl mx-auto">
        {/* 헤더 */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="text-center md:text-left">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              🎲 아조씨 로또
            </h1>
            <p className="text-base md:text-lg text-gray-600">
              공정한 추첨으로 모두에게 기회를!
            </p>
          </div>
          
          <div className="flex gap-2">
            <Link
              to="/lotto/history"
              className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
            >
              📊 내 히스토리
            </Link>
            {user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN' && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 text-white shadow-md hover:shadow-lg hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
              >
                ➕ 새 로또 생성
              </button>
            )}
          </div>
        </div>

        {/* 진행 중인 로또 */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-primary mb-6">
            🔥 진행 중인 로또 <span className="text-accent">{activeLottos.length}</span><span className="text-gray-800">건</span>
          </h2>
          
          {isLoading ? (
            <div className="space-y-6">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : activeLottos.length === 0 ? (
            <EmptyLotto 
              isAdmin={user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN'} 
              onCreateClick={() => setIsCreateModalOpen(true)}
            />
          ) : (
            <div className="space-y-6">
              {activeLottos.map(lotto => {
                const hasApplied = lotto.entries.some(e => e.userId === user?.id);
                const lottoDate = new Date(lotto.date);
                const dayOfWeek = lottoDate.getDay(); // 0=일요일, 6=토요일
                
                // 요일별 색상 (파스텔톤)
                let dateColor = 'text-gray-700'; // 평일 기본
                if (dayOfWeek === 0) dateColor = 'text-red-400'; // 일요일
                else if (dayOfWeek === 6) dateColor = 'text-blue-400'; // 토요일
                // 대체공휴일은 별도 로직 필요 시 추가

                // 관리자 + 신청자 있음 = 스와이프 가능
                const canSwipe = user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN' && lotto.entries.length > 0;
                
                const cardContent = (
                  <div className="card-azocsii">
                    {/* 로또 정보 */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-6">
                      <div className="text-center md:text-left">
                        <div className={`text-3xl md:text-4xl font-bold mb-2 ${dateColor}`}>
                          {format(lottoDate, 'yyyy년 M월 d일', { locale: ko })}
                        </div>
                        <div className={`text-lg md:text-xl ${dateColor}`}>
                          ({format(lottoDate, 'EEEE', { locale: ko })})
                        </div>
                      </div>

                      <div className="flex gap-4 justify-center md:justify-end">
                        <div className="bg-green-50 rounded-xl p-4 text-center">
                          <div className="text-sm text-green-600 mb-1">휴무 가능</div>
                          <div className="text-2xl font-bold text-green-700">{lotto.maxSlots}명</div>
                        </div>
                        <div className="bg-blue-50 rounded-xl p-4 text-center">
                          <div className="text-sm text-blue-600 mb-1">신청 인원</div>
                          <div className="text-2xl font-bold text-blue-700">{lotto.entries.length}명</div>
                        </div>
                      </div>
                    </div>

                    {/* 신청자 목록 */}
                    {lotto.entries.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-600 mb-3">신청자 ({lotto.entries.length}명)</h4>
                        <div className="flex flex-wrap gap-2">
                          {lotto.entries.map(entry => {
                            const isMe = entry.userId === user?.id;
                            return (
                              <div
                                key={entry.id}
                                className={`px-4 py-2 rounded-full text-sm font-bold ${
                                  isMe
                                    ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {entry.user.name}
                                {isMe && ' (나)'}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* 액션 버튼 */}
                    <div className="flex flex-col gap-3">
                      {!hasApplied ? (
                        <button
                          onClick={() => handleApply(lotto.id)}
                          className="w-full bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
                        >
                          🎫 신청하기
                        </button>
                      ) : (
                        <div className="w-full bg-gray-100 text-gray-600 py-4 rounded-xl text-center font-bold text-lg border-2 border-gray-200">
                          ✅ 신청 완료
                        </div>
                      )}

                      {user?.role === 'TEAM_LEADER' || user?.role === 'COMPANY_ADMIN' && lotto.entries.length > 0 && (
                        <button
                          onClick={() => handleDraw(lotto.id)}
                          className="w-full bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 text-white px-6 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
                        >
                          🎰 추첨 시작
                        </button>
                      )}
                    </div>
                  </div>
                );
                
                return (
                  <div key={lotto.id}>
                    {canSwipe ? (
                      <SwipeableCard
                        onApprove={() => handleDraw(lotto.id)}
                      >
                        {cardContent}
                      </SwipeableCard>
                    ) : (
                      cardContent
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* 완료된 로또 (주간 페이지네이션) */}
        {completedLottos.length > 0 && (
          <section>
            {/* 주간 네비게이션 */}
            <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 rounded-xl p-4 text-white mb-6">
              <div className="text-center mb-3">
                <h2 className="text-xl font-bold">
                  ✅ 완료된 로또
                </h2>
                <h3 className="text-lg font-semibold mt-1">
                  {format(currentWeekStart, 'yyyy년 M월', { locale: ko })} {weekOfMonth}주차
                </h3>
                <p className="text-sm opacity-90">
                  {format(currentWeekStart, 'M/d', { locale: ko })} ~ {format(endOfWeek(currentWeekStart, { weekStartsOn: 0 }), 'M/d', { locale: ko })}
                </p>
              </div>
              
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={handlePrevWeek}
                  className="px-2 py-1 text-sm bg-white/20 hover:bg-white/30 rounded-lg transition-all font-medium"
                >
                  ◀ 이전 주
                </button>
                
                <div className="text-center">
                  {!isThisWeek && (
                    <button
                      onClick={handleThisWeek}
                      className="px-3 py-1 text-xs bg-white/30 hover:bg-white/40 rounded-full transition-all"
                    >
                      이번 주로 돌아가기
                    </button>
                  )}
                  <p className="text-xs mt-1">
                    이번 주: {filteredCompletedLottos.length}건
                  </p>
                </div>
                
                <button
                  onClick={handleNextWeek}
                  disabled={isThisWeek}
                  className={`px-2 py-1 text-sm rounded-lg transition-all font-medium ${
                    isThisWeek 
                      ? 'bg-white/10 text-white/50 cursor-not-allowed' 
                      : 'bg-white/20 hover:bg-white/30'
                  }`}
                >
                  다음 주 ▶
                </button>
              </div>
            </div>

            {/* 완료된 로또 목록 */}
            {filteredCompletedLottos.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                <p className="text-6xl mb-4">📭</p>
                <p className="text-lg text-gray-500 mb-2">이번 주 완료된 로또가 없습니다</p>
                <p className="text-sm text-gray-400">다른 주를 확인해보세요</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredCompletedLottos.map(lotto => {
                const winners = lotto.entries.filter(e => e.status === 'WON');
                const losers = lotto.entries.filter(e => e.status === 'LOST');
                const lottoDate = new Date(lotto.date);
                const dayOfWeek = lottoDate.getDay();
                
                // 요일별 색상 (파스텔톤)
                let dateColor = 'text-gray-700'; // 평일 기본
                if (dayOfWeek === 0) dateColor = 'text-red-400'; // 일요일
                else if (dayOfWeek === 6) dateColor = 'text-blue-400'; // 토요일
                
                return (
                  <div
                    key={lotto.id}
                    className="card-azocsii bg-gray-50"
                  >
                    {/* 헤더 */}
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className={`text-2xl font-bold mb-1 ${dateColor}`}>
                          {format(lottoDate, 'M월 d일 (EEE)', { locale: ko })}
                        </div>
                        <div className="text-sm text-gray-500">
                          {format(new Date(lotto.updatedAt), 'M월 d일 HH:mm', { locale: ko })} 추첨 완료
                        </div>
                      </div>
                      <div className="bg-gray-200 text-gray-700 px-4 py-2 rounded-full font-medium">
                        🔚 완료
                      </div>
                    </div>

                    {/* 당첨자 */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-green-600 mb-3">
                        🎉 당첨 ({winners.length}명)
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {winners.map(entry => {
                          const isMe = entry.user.id === user?.id;
                          return (
                            <div
                              key={entry.id}
                              className={`px-4 py-2 rounded-full text-sm font-medium ${
                                isMe
                                  ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white border-2 border-orange-600 shadow-lg'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {entry.user.name}{isMe && ' (나)'} 🏆
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* 미당첨자 */}
                    {losers.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-500 mb-3">
                          미당첨 ({losers.length}명)
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {losers.map(entry => {
                            const isMe = entry.user.id === user?.id;
                            return (
                              <div
                                key={entry.id}
                                className={`px-4 py-2 rounded-full text-sm ${
                                  isMe
                                    ? 'bg-orange-100 text-orange-700 border-2 border-orange-300 font-medium'
                                    : 'bg-gray-200 text-gray-600'
                                }`}
                              >
                                {entry.user.name}{isMe && ' (나)'}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            )}
          </section>
        )}
      </div>

      {/* 새 로또 생성 모달 */}
      <CreateLottoModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateLotto}
      />

      {/* 추첨 애니메이션 */}
      {drawResult && (
        <DrawAnimation
          isOpen={showAnimation}
          winners={drawResult.winners || []}
          currentUserId={user?.id}
          onClose={closeAnimation}
        />
      )}
    </main>
    </FadeInPage>
  );
};

export default LottoPage;
